import { Button, Modal, Textarea, TextInput } from "@mantine/core";
import type React from "react";

export interface NewPetData {
  pet_name: string;
  age: string;
  race: string;
  target_behaviors: string;
}

interface AddPetModalProps {
  opened: boolean;
  petData: NewPetData;
  error: string | null;
  loading: boolean;
  onPetDataChange: (data: NewPetData) => void;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

const AddPetModal: React.FC<AddPetModalProps> = ({
  opened,
  petData,
  error,
  loading,
  onPetDataChange,
  onCancel,
  onSubmit,
}) => (
  <Modal
    opened={opened}
    onClose={onCancel}
    title=""
    withCloseButton={false}
    size="md"
    classNames={{
      content: "rounded-2xl overflow-hidden p-0",
      body: "p-0",
    }}
  >
    <div className="bg-primary text-primary-foreground py-4 sm:py-5 px-4 sm:px-6 text-center">
      <h3 className="m-0 text-lg sm:text-xl font-semibold">Add New Pet</h3>
    </div>
    <form onSubmit={onSubmit}>
      <div className="p-4 sm:p-6">
        <div className="mb-5">
          <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
            Pet Name *
          </label>
          <TextInput
            value={petData.pet_name}
            onChange={(e) => onPetDataChange({ ...petData, pet_name: e.target.value })}
            required
          />
        </div>
        <div className="mb-5">
          <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
            Age (years) *
          </label>
          <TextInput
            type="number"
            value={petData.age}
            onChange={(e) => onPetDataChange({ ...petData, age: e.target.value })}
            required
            min={0}
          />
        </div>
        <div className="mb-5">
          <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">Breed *</label>
          <TextInput
            value={petData.race}
            onChange={(e) => onPetDataChange({ ...petData, race: e.target.value })}
            required
          />
        </div>
        <div className="mb-5">
          <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
            Target Behaviors
          </label>
          <Textarea
            value={petData.target_behaviors}
            onChange={(e) => onPetDataChange({ ...petData, target_behaviors: e.target.value })}
            rows={3}
            placeholder="e.g., leash pulling, jumping on people, separation anxiety..."
          />
        </div>
        {error && (
          <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
            {error}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3 justify-end px-4 sm:px-6 pb-4 sm:pb-6">
        <Button
          type="button"
          variant="default"
          className="min-w-[100px]"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          color="green"
          radius="md"
          className="min-w-[140px]"
          disabled={loading}
        >
          {loading ? "Adding..." : "Add Pet"}
        </Button>
      </div>
    </form>
  </Modal>
);

export default AddPetModal;
